import { useState, useEffect } from "react";
import VirtualKeyboard from "../../components/reusables/virtualKeyboard";
import logo from '../../resources/logo.png';
import CustomerService from "../../rest/customerService";
import AssetsService from "../../rest/assetsService";
import SearchInputWithResults from "../../components/reusables/searchInputWithResults";
import { toast } from "react-toastify";

function PinAuthentication({ onCancel, onSuccess }) {
    const [activeField, setActiveField] = useState(null);
    const [resetOnNextInput, setResetOnNextInput] = useState(false);
    const [user, setUser] = useState("");
    const [loginInput, setLoginInput] = useState("");
    const [pin, setPin] = useState("");
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (activeField) {
            setResetOnNextInput(true);
        }
    }, [activeField]);

    const getKeys = () => {
        if (activeField === "pin") {
            return [1, 2, 3, 4, 5, 6, 7, 8, 9, "back", 0, "ok"];
        }
        if (activeField === "search" || activeField === "user") {
            return "ABCDEFGHIJKLMNOPQRSTUVWXYZ.-_"
                .split("")
                .concat(["back", "ok"]);
        }
        return [];
    };

    const handleKeyPress = (value) => {
        if (!activeField) return;

         const setter = activeField === "search" ? setSearch :
                       activeField === "user" ? setLoginInput : setPin;

        const current = activeField === "search" ? search :
                        activeField === "user" ? loginInput : pin;

        if (value === "back") {
            setter(current.slice(0, -1));
        } else if (value === "ok") {
            setActiveField(null);
        } else {
            const key = String(value).toLowerCase();

            if (resetOnNextInput) {
                setter(key);
                setResetOnNextInput(false);
            } else {
                if (activeField === "pin" && current.length >= 6) return;
                if (
                    (activeField === "search" || activeField === "user") &&
                    current.length >= 20
                )
                    return;
                setter(current + key);
            }
        }
    };

    const checkPin = async () => {
        try {
            const authenticatedUser = await CustomerService.checkStudentPin(loginInput, pin);

            if (authenticatedUser) {
                if (!authenticatedUser.imageData && authenticatedUser.image_url) {
                    try {
                        const imageData = await AssetsService.getProductImage(authenticatedUser.image_url);
                        authenticatedUser.imageData = imageData;
                    } catch {
                        authenticatedUser.imageData = null;
                    }
                }

                onSuccess(user);
            } else {
                toast.error("PIN incorreto.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Erro na autenticação.");
        }
    };

    return (
        <div className="h-screen w-screen bg-white flex flex-col items-center justify-start relative pb-48 md:pb-60">
            <div className="absolute top-6 left-1/2 -translate-x-1/2 flex flex-col items-center">
                <img src={logo} alt="Logo" className="w-24 mb-2" />
                <h1 className="text-3xl font-bold text-custom_blue">Autenticação</h1>
            </div>

            <div className="w-full max-w-lg flex flex-col gap-6 px-6 mt-32">
                <SearchInputWithResults
                    value={search}
                    onSelectUser={(selectedUser) => {
                        setUser(selectedUser);
                        setLoginInput(selectedUser.login);
                    }} placeholder="Pesquisar por nome..."
                    onFocusInput={() => setActiveField("search")}
                    fetchResults={async (input) => {
                        const [studentsResp, employeesResp] = await Promise.all([
                            CustomerService.getAllStudents({
                                search: input,
                                currentPage: 0,
                                pageSize: 10,
                            }),
                            CustomerService.getAllEmployees({
                                search: input,
                                currentPage: 0,
                                pageSize: 10,
                            }),
                        ]);

                        const students = await Promise.all(
                            studentsResp.content.map(async (item) => {
                                if (item.image_url) {
                                    try {
                                        const imageData = await AssetsService.getProductImage(
                                            item.image_url
                                        );
                                        return { ...item, imageData, type: "student" };
                                    } catch {
                                        return { ...item, imageData: null, type: "student" };
                                    }
                                }
                                return { ...item, type: "student", imageData: null };
                            })
                        );

                        const employees = employeesResp.content.map((emp) => ({
                            ...emp,
                            type: "employee",
                            imageData: emp.image_url || null,
                        }));

                        return [...students, ...employees];
                    }}
                />

                <input
                    type="text"
                    placeholder="Usuário"
                    value={loginInput}
                    onFocus={() => setActiveField("user")}
                    readOnly
                    className="w-full p-2 border-2 border-custom_blue rounded-xl text-center shadow-inner focus:outline-none focus:ring-2 focus:ring-custom_green text-xl md:text-2xl bg-white text-black placeholder-gray-400"
                />
                <input
                    type="password"
                    placeholder="Pin"
                    value={pin}
                    onFocus={() => setActiveField("pin")}
                    readOnly
                    className="w-full p-2 border-2 border-custom_blue rounded-xl shadow-inner text-center tracking-widest text-2xl md:text-3xl bg-gray-100 text-black"
                />
            </div>

            <div className="flex items-center justify-evenly">
                <button
                    onClick={checkPin}
                    disabled={pin.length < 4 || !user}
                    className="mt-6 mx-2 px-6 py-4 bg-custom_green disabled:bg-gray-400 text-white text-xl md:text-2xl font-semibold rounded-xl shadow-lg transition-transform transform hover:scale-105 active:scale-95"
                >
                    Confirmar
                </button>

                <button
                    onClick={onCancel}
                    className="mt-6 mx-2 px-6 py-4 bg-custom_red disabled:bg-gray-400 text-white text-xl md:text-2xl font-semibold rounded-xl shadow-lg transition-transform transform hover:scale-105 active:scale-95"
                >
                    Cancelar
                </button>
            </div>

            {activeField && (
                <VirtualKeyboard keys={getKeys()} onInput={handleKeyPress} />
            )}
        </div>
    );
}

export default PinAuthentication;
